import React from 'react';
import { useTranslation } from 'react-i18next';
import '../../styles/ErrorBoundary.css';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({
      error,
      errorInfo
    });

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });

    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
          onReload={this.handleReload}
          showDetails={this.props.showDetails !== false && process.env.NODE_ENV === 'development'}
        />
      );
    }

    return this.props.children;
  }
}

function ErrorFallback({ error, errorInfo, onReset, onReload, showDetails = false }) {
  const { t } = useTranslation();
  const [detailsExpanded, setDetailsExpanded] = React.useState(false);

  return (
    <div className="error-boundary-fallback">
      <div className="error-content">
        <div className="error-icon-wrapper">
          <span className="error-icon">⚠️</span>
        </div>

        <h2 className="error-title">
          {t('errorBoundary.title')}
        </h2>
        <p className="error-description">
          {t('errorBoundary.description')}
        </p>

        <div className="error-actions">
          <button
            className="error-btn error-btn-primary"
            onClick={onReload}
          >
            {t('errorBoundary.reload')}
          </button>
          <button
            className="error-btn error-btn-secondary"
            onClick={onReset}
          >
            {t('errorBoundary.tryAgain')}
          </button>
        </div>

        {showDetails && error && (
          <div className="error-details">
            <button
              className="error-details-toggle"
              onClick={() => setDetailsExpanded(!detailsExpanded)}
            >
              {detailsExpanded ? '▼' : '▶'} {t('errorBoundary.showDetails')}
            </button>
            {detailsExpanded && (
              <div className="error-details-content">
                <div className="error-message">
                  <strong>{t('errorBoundary.error')}:</strong>
                  <pre>{error.toString()}</pre>
                </div>
                {error.stack && (
                  <div className="error-stack">
                    <strong>{t('errorBoundary.stackTrace')}:</strong>
                    <pre>{error.stack}</pre>
                  </div>
                )}
                {errorInfo && errorInfo.componentStack && (
                  <div className="error-component-stack">
                    <strong>{t('errorBoundary.componentStack')}:</strong>
                    <pre>{errorInfo.componentStack}</pre>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <p className="error-support">
          {t('errorBoundary.support')}
        </p>
      </div>
    </div>
  );
}

export default ErrorBoundary;
